import { useState, type ReactElement } from 'react';
import type { Message, Uuid } from '@zenda/api-contract';
import { Icon, type IconName } from './Icon';
import { formatDateShort, formatTime } from '../lib/format';
import { useT } from '../lib/i18n';
import { useTip } from './Tip';
import { deliveryState as deliveryLabel } from '../lib/labels';
import { baixarAnexo, useMidia } from '../lib/media';
import { usePlayer } from './Player';
import { reaisDeMicros } from '../lib/precos-meta';
import { useMostrarCustos } from '../lib/mostrar-custos';
import '../styles/piloto.css';

/**
 * `smb_echo` e uma mensagem que o medico mandou pelo aplicativo dele, capturada
 * por coexistencia. Nao passou pela nossa API: nao existe status de entrega para
 * ela, e fingir um icone de "entregue" seria mentira. Por isso o rotulo e a
 * ausencia de icone sao regra, nao enfeite.
 */
function DeliveryMark({ message }: { readonly message: Message }): ReactElement | null {
  const t = useT();
  const tip = useTip();
  if (message.origin === 'smb_echo') return null;
  const { deliveryState } = message;
  if (deliveryState === 'none') return null;

  if (deliveryState === 'failed') {
    return (
      <span className="bubble__fail">
        <Icon name="alert" size={13} />
        {t.inbox.failed}
        {message.failureCode === null ? '' : ` (${message.failureCode})`}
      </span>
    );
  }

  const seen = deliveryState === 'read' || deliveryState === 'played';
  const twice = seen || deliveryState === 'delivered';
  return (
    <span
      className={`bubble__mark${seen ? ' is-read' : ''}`}
      {...tip(deliveryLabel(t, deliveryState))}
    >
      <Icon name={twice ? 'checks' : 'check'} size={14} />
    </span>
  );
}

function CustoDaMensagem({ message }: { readonly message: Message }): ReactElement | null {
  const t = useT();
  const tip = useTip();
  const [mostrar] = useMostrarCustos();
  const custo = message.cost;
  if (!mostrar || custo === null || custo.totalMicros === 0) return null;

  const parceiro = reaisDeMicros(custo.partnerMicros);
  const meta =
    custo.partnerMicros === 0
      ? `Meta ${reaisDeMicros(custo.metaMicros)}`
      : t.custo.partes(reaisDeMicros(custo.metaMicros), parceiro);
  const categoria = custo.category === null ? undefined : t.custo.categoria[custo.category];
  const detalhe = custo.chargedByMeta
    ? t.custo.daMensagem(categoria === undefined ? meta : `${meta} (${categoria})`)
    : t.custo.gratisNaMeta(parceiro);

  return (
    <span className="bubble__custo num" {...tip(detalhe)}>
      {reaisDeMicros(custo.totalMicros)}
    </span>
  );
}

const ANEXO: Readonly<Record<string, IconName>> = {
  image: 'image',
  audio: 'mic',
  video: 'video',
  document: 'note',
  sticker: 'sparkle',
  location: 'signal',
  contacts: 'user',
};

function tamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

/**
 * O anexo.
 *
 * ── Por que a prévia existe sem a gente guardar nada ──
 * `GET /api/messages/{id}/media` pede a URL à Meta no instante do pedido e
 * repassa os bytes. Nada é gravado: nem disco, nem bucket. O arquivo continua
 * sendo da Meta, e some de lá — por isso o rodapé diz até quando, e o botão de
 * baixar existe. Quem quer guardar para sempre guarda no computador.
 *
 * ── Por que o prazo aparece na tela ──
 * Sete dias para o que o paciente mandou, trinta para o que saiu daqui ✅. Sem
 * isso a atendente descobriria o limite no dia em que o exame que ela precisava
 * já não abre.
 */
function Anexo({
  message,
  contato,
  conversaId,
}: {
  readonly message: Message;
  readonly contato: string;
  readonly conversaId: Uuid;
}): ReactElement | null {
  const t = useT();
  const icone = ANEXO[message.waType];
  if (icone === undefined) return null;

  const rotulo = t.enums.waType[message.waType] ?? message.waType;
  const media = message.media;

  // Anexo sem `media` é o legado de antes da 0014: mostra o tipo e para por aí,
  // porque não há id para pedir os bytes.
  if (media === null) {
    return (
      <p className="anexo__so-tipo">
        <Icon name={icone} size={14} />
        {rotulo}
      </p>
    );
  }

  return (
    <AnexoCard
      message={message}
      media={media}
      icone={icone}
      rotulo={rotulo}
      contato={contato}
      conversaId={conversaId}
    />
  );
}

function AnexoCard({
  message,
  media,
  icone,
  rotulo,
  contato,
  conversaId,
}: {
  readonly message: Message;
  readonly media: NonNullable<Message['media']>;
  readonly icone: IconName;
  readonly rotulo: string;
  readonly contato: string;
  readonly conversaId: Uuid;
}): ReactElement {
  const t = useT();
  const tip = useTip();
  const player = usePlayer();
  const ehImagem = message.waType === 'image' || message.waType === 'sticker';
  const ehAudio = message.waType === 'audio';

  /**
   * Só a IMAGEM pré-carrega.
   *
   * Áudio saiu daqui quando ganhou o player global: baixar o arquivo ao
   * renderizar o balão gastaria duas chamadas de rede por áudio da conversa
   * para talvez ninguém ouvir nenhum. O download passa a acontecer no play.
   */
  const previa = useMidia(message.id, ehImagem);
  const [baixando, setBaixando] = useState(false);
  const [erroBaixa, setErroBaixa] = useState<string | null>(null);

  const expira = t.inbox.mediaExpires(formatDateShort(media.expiresAt));
  const nome = media.filename ?? rotulo;
  const estaTocando = player.atual?.messageId === message.id;
  // Quem mandou, para o player dizer de quem é o áudio que está tocando.
  const autor = message.direction === 'in' ? contato : (message.authorName ?? t.player.audio);

  async function baixar(): Promise<void> {
    setBaixando(true);
    setErroBaixa(null);
    try {
      await baixarAnexo(message.id, media.filename);
    } catch (e) {
      setErroBaixa(e instanceof Error ? e.message : t.ui.falhaAoBaixar);
    } finally {
      setBaixando(false);
    }
  }

  return (
    <div className={`anexo${ehImagem ? ' anexo--img' : ''}`}>
      {ehImagem ? (
        <div className="anexo__moldura">
          {previa.url !== null ? (
            <a href={previa.url} target="_blank" rel="noreferrer">
              <img src={previa.url} alt={rotulo} />
            </a>
          ) : (
            <div className={`anexo__vazio${previa.erro === null ? '' : ' is-erro'}`}>
              <Icon name={previa.erro === null ? 'image' : 'alert'} size={18} />
              <span>{previa.erro ?? t.common.loading}</span>
            </div>
          )}
        </div>
      ) : ehAudio ? (
        <button
          type="button"
          className={`anexo__ouvir${estaTocando ? ' is-on' : ''}`}
          onClick={() =>
            player.tocar({
              messageId: message.id,
              conversaId,
              titulo: autor,
              subtitulo: formatTime(message.occurredAt),
            })
          }
        >
          <span className="anexo__ouvir-ico">
            <Icon name={estaTocando && player.tocando ? 'pause' : 'play'} size={15} />
          </span>
          <span className="anexo__meta">
            <span className="anexo__nome">{t.player.ouvir}</span>
            <span className="anexo__sub">
              {media.sizeBytes === null ? rotulo : `${rotulo} · ${tamanho(media.sizeBytes)}`}
            </span>
          </span>
          {/* Onda decorativa, com o movimento só na faixa que está tocando. É o
              que distingue "este áudio" de "um áudio" numa conversa com vários. */}
          <span className="anexo__onda" aria-hidden="true">
            {[6, 12, 9, 15, 7, 11, 5].map((h, i) => (
              <i key={`${h}-${i}`} style={{ height: h }} />
            ))}
          </span>
        </button>
      ) : (
        <div className="anexo__arq">
          <span className="anexo__ico">
            <Icon name={icone} size={17} />
          </span>
          <span className="anexo__meta">
            <span className="anexo__nome" {...tip(nome)}>
              {nome}
            </span>
            <span className="anexo__sub">
              {media.sizeBytes === null ? rotulo : `${rotulo} · ${tamanho(media.sizeBytes)}`}
            </span>
          </span>
        </div>
      )}

      <div className="anexo__pe">
        {/* O prazo não é enfeite: sem ele a atendente descobre o limite no dia
            em que o exame que ela precisava já não abre. */}
        <span className="anexo__prazo">{erroBaixa ?? expira}</span>
        <button
          type="button"
          className="anexo__baixar"
          onClick={() => void baixar()}
          disabled={baixando}
        >
          <Icon name="download" size={13} />
          {baixando ? t.common.loading : t.inbox.download}
        </button>
      </div>
    </div>
  );
}

export function MessageBubble({
  message,
  contato,
  conversaId,
  onCorrigir,
}: {
  readonly message: Message;
  /** Nome do paciente — o player precisa dizer de quem é o áudio. */
  readonly contato: string;
  /** Para onde o player volta quando ela clica em ir para a conversa. */
  readonly conversaId: Uuid;
  /** Mensagem do piloto: abre o texto dela no campo para a atendente corrigir. */
  readonly onCorrigir?: (message: Message) => void;
}): ReactElement {
  const t = useT();
  const tip = useTip();

  if (message.isInternalNote) {
    return (
      <div className="note">
        <p className="note__label">
          <Icon name="note" size={13} />
          {t.inbox.internalNoteLabel}
          {message.authorName === null ? '' : ` · ${message.authorName}`}
        </p>
        <p className="note__body">{message.body}</p>
        <span className="note__time num">{formatTime(message.occurredAt)}</span>
      </div>
    );
  }

  const outgoing = message.direction === 'out';
  const echo = message.origin === 'smb_echo';

  return (
    <div className={`bubble-line${outgoing ? ' is-out' : ''}`} data-msg={message.id}>
      <div className={`bubble${outgoing ? ' bubble--out' : ' bubble--in'}`}>
        {echo ? (
          <p className="bubble__origin">
            <Icon name="phone" size={12} />
            {t.inbox.sentFromPhone}
          </p>
        ) : null}
        {outgoing && !echo && message.authorName !== null ? (
          <p className="bubble__author">{message.authorName}</p>
        ) : null}
        {message.byAutopilot ? (
          <p className="bubble__author">
            <span className="bubble__piloto" {...tip(t.piloto.seloDica)}>
              <Icon name="robot" size={10} />
              {t.piloto.selo}
            </span>
            {onCorrigir === undefined || message.body === null ? null : (
              <button
                type="button"
                className="bubble__corrigir"
                onClick={() => onCorrigir(message)}
                {...tip(t.piloto.corrigirDica)}
              >
                {t.piloto.corrigir}
              </button>
            )}
          </p>
        ) : null}
        <Anexo message={message} contato={contato} conversaId={conversaId} />
        {message.body !== null ? (
          <p className="bubble__body">{message.body}</p>
        ) : ANEXO[message.waType] === undefined ? (
          <p className="anexo__so-tipo">
            <Icon name="alert" size={14} />
            {t.inbox.naoSuportada}
          </p>
        ) : null}
        <span className="bubble__meta">
          <CustoDaMensagem message={message} />
          <span className="num">{formatTime(message.occurredAt)}</span>
          <DeliveryMark message={message} />
        </span>
      </div>
    </div>
  );
}
